import * as React from 'react';
import { connect } from 'react-redux';
import { priceAvailability } from '../../helpers/priceHelpers';
import { __ } from '../../helpers/TranslationService';
import { ICabinInfo, ICruiseReducer } from '../../interfaces';
import { IState } from '../../reducers';
import { CabinDescription } from './CabinDescription';
import { CabinOptionComponent, CabinValueComponent } from './CabinDetailsCustomSelect';
import { CabinPriceSection } from './CabinPriceSection';
import loadable from '@loadable/component';
import { ImgWithFallback } from '../ImgWithFallback';

const CustomSelect = loadable(() => import('../CustomSelect/CustomSelect'));

interface IStateToProps {
    cruisePage: ICruiseReducer;
    discountThreshold: number;
}

export interface ICabinDetailsOverlayProps {
    cabin: ICabinInfo;
    cabins: ICabinInfo[];
    urlBase: string;
    isPriceShown?: boolean;
    isCabinSelectShown?: boolean;
    nPassengers?: number;
}

interface IComponentState {
    selectedCabinIndex: number;
}

export class CabinDetailsOverlayClass extends React.Component<
    ICabinDetailsOverlayProps & IStateToProps,
    IComponentState
> {
    constructor(props) {
        super(props);
        this.state = {
            selectedCabinIndex: this.props.isPriceShown ? this.props.cabin?.cabinType.nid : 0,
        };
    }
    getSelectedCabin() {
        const index = this.props.cabins.findIndex(
            (item) => item.cabinType.nid === this.state.selectedCabinIndex,
        );
        return index > 0 ? this.props.cabins[index] : this.props.cabins[0];
    }
    handleCabinChange = (index: number) => this.setState({ selectedCabinIndex: index });

    getLabel = (cabin) =>
        cabin
            ? cabin.cabinType.labels.labelForRequestForm
                ? cabin.cabinType.labels.labelForRequestForm
                : cabin.cabinType.labels.withoutPrice
            : '';

    getSelect() {
        const { cabins, isPriceShown, cruisePage } = this.props;
        const { sails, selectedSailIndex, soldOutBooking } = cruisePage;

        const selectedCabin = this.getSelectedCabin();

        const options = cabins.reduce((acc, cabin) => {
            const soldOutType = priceAvailability(
                sails?.[selectedSailIndex]?.departure,
                soldOutBooking,
                cabin.perPersonPrice,
                cabin.currency,
            ).string;

            const cabinTypeId = cabin.cabinType.nid;
            const cabinLabel = this.getLabel(cabin);

            // de-duplication
            if (acc.some((item) => item.label === cabinLabel || item.id === cabinTypeId))
                return acc;

            return [
                ...acc,
                {
                    id: cabinTypeId,
                    label: this.getLabel(cabin),
                    value: this.getLabel(cabin),
                    maxDiscount: cabin.discountPercentage || undefined, // Do not show 0
                },
            ];
        }, []);
        const label = this.getLabel(selectedCabin);
        const value = { label, value: label, id: selectedCabin?.cabinType.nid };

        return (
            <CustomSelect
                value={value}
                options={options}
                valueComponent={CabinValueComponent}
                optionComponent={CabinOptionComponent}
                onChange={this.handleCabinChange}
                clearable={false}
                searchable={false}
                style={{ margin: '20px auto 40px auto', maxWidth: '525px' }}
            />
        );
    }

    render() {
        const {
            urlBase,
            cabins,
            isCabinSelectShown,
            isPriceShown,
            discountThreshold,
            cruisePage,
            nPassengers,
        } = this.props;
        const { sails, soldOutBooking, selectedSailIndex } = cruisePage;
        const selectedCabin = this.getSelectedCabin();

        if (!selectedCabin) return null;

        const cabin = selectedCabin?.cabinType;
        const url = `${urlBase}/${cabin?.nid}`;

        const cabinText = __(
            'The price of the chosen cabin depends on your cabin location, the equipment criteria and the size.',
            'dreamlines',
        );

        return (
            <div className="cabinOverlay">
                <div>
                    <h4>{cabin.title}</h4>
                    <div className="cabinNotes">{cabinText}</div>
                    {isCabinSelectShown && this.getSelect()}
                </div>
                <div className="cabinData">
                    <div className="cabinMain">
                        <div className="cabinImage">
                            <ImgWithFallback
                                imageFallback="cabin"
                                imageUrl={cabin.image.original}
                                alt={cabin.title}
                            />
                        </div>
                        <div className="mainText">
                            <div className="iconNumbers">
                                <CabinDescription title="Location" text={cabin.location} />
                                <CabinDescription
                                    title="Occupancy"
                                    text={cabin?.maxPassengers.toString()}
                                />

                                <CabinDescription title="Cabin size" text={cabin.size} />
                            </div>

                            {cabin.isGuaranteeCabin && <p>{cabin.guaranteeCabinInfo}</p>}
                        </div>
                    </div>

                    <div className="cabinDetails">
                        <CabinDescription title="Amenities" text={cabin.amenities} />
                        <CabinDescription
                            title="Following advantages are included"
                            text={cabin.advantages}
                        />
                        {isPriceShown && (
                            <CabinPriceSection
                                url={url}
                                currency={selectedCabin.currency}
                                perPersonPrice={selectedCabin.perPersonPrice}
                                discountPercentage={selectedCabin.discountPercentage}
                                isButtonShown={isPriceShown && isCabinSelectShown}
                                departure={
                                    sails.length > 0 ? sails[selectedSailIndex].departure : null
                                }
                                soldOutBooking={soldOutBooking}
                                discountThreshold={discountThreshold}
                                cabin={selectedCabin}
                                nPassengers={nPassengers}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: IState): IStateToProps => ({
    cruisePage: state.cruisePage,
    discountThreshold: state.pageData.discountThreshold,
});

export const CabinDetailsOverlay = connect(mapStateToProps)(CabinDetailsOverlayClass);
export default CabinDetailsOverlay;
